import {useCurrentRepository} from '@github-ui/current-repository'
import {repositoryPropertiesSettingsPath} from '@github-ui/paths'
import {useAnalytics} from '@github-ui/use-analytics'
import {useLocalStorage} from '@github-ui/use-safe-storage/local-storage'
import {Banner} from '@primer/react'
import {useCallback, useEffect, useState} from 'react'

const DISMISS_TTL_MS = 5 * 24 * 60 * 60 * 1000 // 5 days

export function RequiredCustomPropertiesBanner({missingCount}: {missingCount: number}) {
  if (missingCount <= 0) return null

  return <RequiredCustomPropertiesBannerContent missingCount={missingCount} />
}

function RequiredCustomPropertiesBannerContent({missingCount}: {missingCount: number}) {
  const repo = useCurrentRepository()
  const {isDismissed, remindMeLater} = useRemindMeLater(dismissedStorageKey(repo.ownerLogin, repo.name), DISMISS_TTL_MS)
  const {sendAnalyticsEvent} = useAnalytics()

  useEffect(() => {
    sendAnalyticsEvent('repository_overview.required_custom_properties_banner.shown')
  }, [sendAnalyticsEvent])

  if (isDismissed) return null

  const setValuesPath = repositoryPropertiesSettingsPath({org: repo.ownerLogin, repo: repo.name})

  const title =
    missingCount === 1
      ? 'This repository is missing a required custom property value'
      : `This repository is missing ${missingCount} required custom property values`

  return (
    <Banner
      variant="warning"
      title={title}
      description="Set the required custom property values to keep this repository compliant with your organization's policies."
      primaryAction={
        <Banner.PrimaryAction
          as="a"
          href={setValuesPath}
          onClick={() => sendAnalyticsEvent('repository_overview.required_custom_properties_banner.clicked')}
        >
          Set values
        </Banner.PrimaryAction>
      }
      secondaryAction={
        <Banner.SecondaryAction
          onClick={() => {
            remindMeLater()
            sendAnalyticsEvent('repository_overview.required_custom_properties_banner.dismissed')
          }}
        >
          Remind me later
        </Banner.SecondaryAction>
      }
    />
  )
}

function dismissedStorageKey(ownerLogin: string, name: string) {
  return `repos-overview:required-custom-properties-banner:dismissed:${ownerLogin}/${name}`
}

function useRemindMeLater(storageKey: string, ttlMs: number): {isDismissed: boolean; remindMeLater: () => void} {
  const [dismissedUntil, setDismissedUntil] = useLocalStorage<number | null>(storageKey, null)
  const [mountedAt] = useState(() => Date.now())
  const isDismissed = dismissedUntil !== null && mountedAt < dismissedUntil

  const remindMeLater = useCallback(() => {
    setDismissedUntil(Date.now() + ttlMs, {flush: true})
  }, [setDismissedUntil, ttlMs])

  return {isDismissed, remindMeLater}
}
